import { getFolders, getFolderById } from '../collectionFolders';
import { normalizeFolderVariables } from '../folderVariables';
import { normalizeRequestBodyMode } from '../requestBodyMode';
import { getFormDataFieldsFromRequest } from '../multipart';

/**
 * Export a sidebar collection to a Bruno directory: a set of `.bru` text files
 * (one per request, nested under folder directories) plus a `bruno.json` manifest.
 * Returns `{ files: [{ path, content }] }` — Bruno is a folder-on-disk format, so
 * the caller writes each file (Tauri dir write / browser multi-download).
 *
 * The `.bru` block layout is what Bruno itself writes (`params:query`, apikey `placement`,
 * `auth: inherit` vs `auth: none`), so the files open correctly in Bruno AND survive a
 * round-trip through this app's own Bruno importer.
 */

function sanitizeSegment(name) {
  return String(name || 'untitled').replace(/[^\w.-]+/g, '_').slice(0, 80) || 'untitled';
}

/** A key with a `:` (or a leading `~`/`"`) must be quoted to parse back. */
function bruVarKey(key) {
  return /[:]|^[~"]/.test(key) ? JSON.stringify(key) : key;
}

function block(name, lines) {
  return `${name} {\n${lines.map((l) => `  ${l}`).join('\n')}\n}`;
}

function kvLines(rows) {
  return (rows || [])
    .filter((r) => r.key)
    .map((r) => `${r.enabled === false ? '~' : ''}${r.key}: ${r.value ?? ''}`);
}

function authBlocks(auth, warnings) {
  // Real Bruno semantics: `auth: inherit` inherits from the folder/collection, `auth: none` sends
  // NO auth. Writing `none` for inherit (as this did until 28.09.2026) made Bruno — and our own
  // importer — drop the parent auth. A request with no auth object defaults to inherit, as in the app.
  if (!auth || !auth.type || auth.type === 'inherit') return { mode: 'inherit', text: '' };
  if (auth.type === 'noauth') return { mode: 'none', text: '' };
  switch (auth.type) {
    case 'bearer':
      return { mode: 'bearer', text: block('auth:bearer', [`token: ${auth.bearer ?? ''}`]) };
    case 'basic':
      return {
        mode: 'basic',
        text: block('auth:basic', [`username: ${auth.basic?.username ?? ''}`, `password: ${auth.basic?.password ?? ''}`]),
      };
    case 'apikey':
      return {
        mode: 'apikey',
        text: block('auth:apikey', [
          `key: ${auth.apikey?.key ?? ''}`,
          `value: ${auth.apikey?.value ?? ''}`,
          `placement: ${auth.apikey?.in === 'query' ? 'queryparams' : 'header'}`,
        ]),
      };
    default:
      warnings?.push(`Bruno export: auth type "${auth.type}" is not supported and was dropped.`);
      return { mode: 'none', text: '' };
  }
}

function bodyBlocks(request, warnings) {
  const { bodyType, rawBodyLanguage } = normalizeRequestBodyMode(request);
  if (bodyType === 'none') return { mode: 'none', text: '' };

  if (bodyType === 'urlencoded') {
    const rows = getFormDataFieldsFromRequest(request).filter((f) => f.key);
    return {
      mode: 'formUrlEncoded',
      text: block('body:form-urlencoded', kvLines(rows)),
    };
  }
  if (bodyType === 'form-data') {
    warnings?.push('Bruno export: multipart/form-data is not round-tripped by this app\'s Bruno importer.');
    return { mode: 'multipartForm', text: '' };
  }
  if (bodyType === 'graphql') {
    warnings?.push('Bruno export: GraphQL body is written as text (this app\'s Bruno importer does not parse graphql blocks).');
    return { mode: 'text', text: block('body:text', String(request.body || '').split('\n')) };
  }

  const raw = String(request.body || '');
  if (rawBodyLanguage === 'json') return { mode: 'json', text: block('body:json', raw.split('\n')) };
  if (rawBodyLanguage === 'xml') return { mode: 'xml', text: block('body:xml', raw.split('\n')) };
  return { mode: 'text', text: block('body:text', raw.split('\n')) };
}

function requestToBru(request, seq, warnings) {
  const method = (request.method || 'GET').toLowerCase();
  const auth = authBlocks(request.auth, warnings);
  const body = bodyBlocks(request, warnings);

  const parts = [];
  parts.push(block('meta', [
    `name: ${request.name || 'Request'}`,
    'type: http',
    `seq: ${seq}`,
  ]));
  parts.push(block(method, [
    `url: ${request.url || ''}`,
    `body: ${body.mode}`,
    `auth: ${auth.mode}`,
  ]));

  const headerLines = kvLines(request.headers);
  if (headerLines.length) parts.push(block('headers', headerLines));
  const queryLines = kvLines(request.params);
  if (queryLines.length) parts.push(block('params:query', queryLines));
  if (auth.text) parts.push(auth.text);
  if (body.text) parts.push(body.text);

  const pre = (request.preRequestScript ?? request.pre_request_script ?? '').trim();
  const test = (request.testScript ?? request.test_script ?? '').trim();
  if (pre) parts.push(block('script:pre-request', pre.split('\n')));
  if (test) parts.push(block('tests', test.split('\n')));

  return parts.join('\n\n') + '\n';
}

/**
 * @param {object} collection Sidebar collection
 * @param {{ push: (msg: string) => void }} warnings
 * @returns {{ files: Array<{ path: string, content: string }> }}
 */
export function toBrunoCollection(collection, warnings) {
  const files = [];
  files.push({
    path: 'bruno.json',
    content: JSON.stringify(
      { version: '1', name: collection.name || 'Collection', type: 'collection', ignore: ['node_modules', '.git'] },
      null,
      2
    ),
  });

  // Directory path for a folder id, built from the folder ancestry.
  const folderDir = (folderId) => {
    if (folderId == null) return '';
    const segments = [];
    const seen = new Set();
    let current = folderId;
    while (current != null && !seen.has(String(current))) {
      seen.add(String(current));
      const folder = getFolderById(collection, current);
      if (!folder) break;
      segments.unshift(sanitizeSegment(folder.name));
      current = folder.parent_id ?? null;
    }
    return segments.join('/');
  };

  // A folder.bru marker per folder (Bruno stores folder metadata this way).
  for (const folder of getFolders(collection)) {
    const dir = folderDir(folder.id);
    // Folder vars as `vars:pre-request`, `~` for a disabled row — the inverse
    // of fromBru's folder defaults (bruLang parseBruPairs).
    // A SECRET folder row is written without its value: Bruno folder vars have
    // no secret marker, and the file must never carry it (tester F1).
    const vars = normalizeFolderVariables(folder.variables).map(
      (r) => `${r.enabled ? '' : '~'}${bruVarKey(r.key)}: ${r.secret ? '' : r.value.replace(/\r?\n/g, ' ')}`
    );
    let content = block('meta', [`name: ${folder.name || 'Folder'}`, 'seq: 1']) + '\n';
    if (vars.length) content += '\n' + block('vars:pre-request', vars) + '\n';
    files.push({
      path: dir ? `${dir}/folder.bru` : `${sanitizeSegment(folder.name)}/folder.bru`,
      content,
    });
  }

  const nameCounts = new Map();
  (collection.requests || []).forEach((req, idx) => {
    const dir = folderDir(req.folder_id ?? req.settings?.folder_id ?? null);
    let base = sanitizeSegment(req.name);
    // De-dupe filenames within the same directory.
    const dedupeKey = `${dir}//${base}`;
    const count = nameCounts.get(dedupeKey) || 0;
    nameCounts.set(dedupeKey, count + 1);
    if (count > 0) base = `${base}_${count}`;
    const path = dir ? `${dir}/${base}.bru` : `${base}.bru`;
    files.push({ path, content: requestToBru(req, idx + 1, warnings) });
  });

  return { files };
}
